// Shared helpers for every file-upload form in the app.

export const MAX_FILE_MB = 25;
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
export const MAX_PHOTOS = 10;
export const MAX_VOICE = 10;
export const MAX_ATTACHMENTS = 10;

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|avif)$/i;
const ATTACH_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|avif|pdf|mp3|m4a|wav|ogg|webm|aac|mp4|mov|m4v)$/i;

// Some phones/browsers (HEIC on desktop, drag-drop on Windows) give an empty file.type,
// so fall back to the extension instead of silently dropping the file.
export const isImageFile = (file) =>
  !!file && ((file.type || "").startsWith("image/") || (!file.type && IMAGE_EXT.test(file.name || "")));

export const isAttachmentFile = (file) =>
  !!file &&
  (/^(image|audio|video)\//.test(file.type || "") || file.type === "application/pdf" ||
    (!file.type && ATTACH_EXT.test(file.name || "")));

export const tooBig = (file) => !!file && file.size > MAX_FILE_BYTES;

// Splits a FileList into accepted files and human-readable problems.
// `room` = how many more files may still be added.
export function filterFiles(fileList, { room = Infinity, accept = isAttachmentFile, label = "file" } = {}) {
  const list = Array.from(fileList || []);
  const accepted = [];
  const problems = [];
  let wrongType = 0;
  let oversize = 0;
  let overLimit = 0;
  for (const f of list) {
    if (!accept(f)) { wrongType++; continue; }
    if (tooBig(f)) { oversize++; continue; }
    if (accepted.length >= room) { overLimit++; continue; }
    accepted.push(f);
  }
  if (wrongType) problems.push(`${wrongType} ${label}${wrongType > 1 ? "s were" : " was"} skipped (unsupported type).`);
  if (oversize) problems.push(`${oversize} ${label}${oversize > 1 ? "s were" : " was"} skipped (over ${MAX_FILE_MB} MB).`);
  if (overLimit) problems.push(`Only ${Math.max(room, 0) === Infinity ? "" : Math.max(room, 0) + " more "}${label}${room === 1 ? "" : "s"} can be added; ${overLimit} skipped.`);
  return { accepted, problems };
}

export const pickAudioType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
};

export const audioExt = (type = "") => (type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm");

// Turns an axios error into something a person can act on.
export function uploadErrorMessage(err, fallback = "Upload failed. Please try again.") {
  if (err?.code === "ECONNABORTED") return "The upload timed out. Check your connection or send fewer / smaller files.";
  if (!err?.response) return "Couldn't reach the server. Check your internet connection and try again.";
  const { status, data } = err.response;
  if (status === 413) return `The files are too large for the server. Keep each under ${MAX_FILE_MB} MB or send fewer at once.`;
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (typeof data === "string" && data.length < 200 && !data.trim().startsWith("<")) return data;
  return data?.message || data?.error || fallback;
}
